import { t } from '@lingui/core/macro';
import {
  BookingProposalType,
  BookingRecordDetailsCustomer,
  CarWheelIdentifiers,
  Country,
  LicensePlate,
  SalesItemBookingCategoryType
} from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { SALES_ITEM_BOOKING_FLOW_TYPE, SelectedSalesItemsCar } from 'noddi-types';
import { NoddiInfoBanner } from 'noddi-ui-web';
import { getIsTireServiceJob, NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import {
  useAuthContext,
  useBookingAddress,
  useBookingCars,
  useBookingSelectedSalesItemsCars,
  useBookingTimeWindow
} from 'noddi-web';

import {
  useBookingProposalAddress,
  useBookingProposalItems,
  useBookingProposalTimeWindow,
  useBookingProposalType
} from '../../../stores/bookingProposalStore';
import {
  useStationaryAddonServices,
  useStationaryBookingCar,
  useStationaryPrimaryService,
  useStationaryReservation
} from '../../../stores/StationaryBookingStore';
import { spikedTireBookingSeason, type SpikedTireBookingSeasonDecision } from './spikedTireBookingSeason';

const countryValues: readonly string[] = Object.values(Country);

const toCountry = (code: string | null | undefined): Country | null => {
  if (code == null || !countryValues.includes(code)) {
    return null;
  }
  return code as Country;
};

const apiDay = (value: string | null | undefined): string | null => {
  if (value == null || value === '') {
    return null;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  return noddiFormatDate(new Date(value), NoddiDateFormats.API_DATE);
};

const dateLabel = (apiDate: string): string => noddiFormatDate(new Date(`${apiDate}T12:00:00`), NoddiDateFormats.DATE);

type PlateQuery = {
  data?: { carWheelIdentifiers?: { identifiers?: readonly string[] } | null };
  isLoading: boolean;
  isError: boolean;
};

const usePlateStudded = (plate: LicensePlate | undefined, enabled: boolean): PlateQuery =>
  noddiAsync.useGet({
    type: URLKeys.getCarFromLicensePlateNumber,
    input: { number: plate?.number ?? '', countryCode: plate?.countryCode ?? Country.NOR },
    queryConfig: { enabled: enabled && plate != null }
  });

/**
 * True when one of the first three plates has spiked winter tires.
 * Bookings with more cars than that are not checked past the third.
 */
const useHasStuddedWinterTires = (plates: LicensePlate[], enabled: boolean): boolean | undefined => {
  const { isLoggedIn } = useAuthContext();
  const canLookup = enabled && isLoggedIn;
  const first = usePlateStudded(plates[0], canLookup);
  const second = usePlateStudded(plates[1], canLookup);
  const third = usePlateStudded(plates[2], canLookup);
  if (!canLookup) {
    return false;
  }
  const queried = [first, second, third].filter((_, index) => plates[index] != null);
  if (queried.some((query) => query.isLoading)) {
    return undefined;
  }
  if (queried.some((query) => query.data?.carWheelIdentifiers?.identifiers?.includes(CarWheelIdentifiers.IS_STUDDED))) {
    return true;
  }
  if (queried.some((query) => query.isError)) {
    return undefined;
  }
  return false;
};

const isWheelChangeOrTireHotel = (items: SelectedSalesItemsCar[]): boolean =>
  items.some(
    (item) =>
      item.bookingCategoryType === SalesItemBookingCategoryType.WHEEL_SERVICES ||
      item.type === SALES_ITEM_BOOKING_FLOW_TYPE.TIRE_HOTEL_PICKUP
  );

type Props = {
  /** Wheel change or tire hotel, and the car has spiked tires. */
  active: boolean;
  countryCode?: string | null;
  zipCode?: string | null;
  /** Chosen booking day. Null shows the rule without saying a date is outside it. */
  bookingDate?: string | null;
};

const SpikedTireSeasonBanner = ({ active, countryCode, zipCode, bookingDate = null }: Props) => {
  const country = toCountry(countryCode);
  // The window is public law, so a proposal page can show it before login.
  const canFetch = active && country != null;
  const day = apiDay(bookingDate);

  const seasonQuery = noddiAsync.useGet({
    type: URLKeys.getStuddedTireSeason,
    input: {
      country: country ?? Country.NOR,
      zipCode: zipCode ?? undefined,
      asOf: day ?? undefined
    },
    queryConfig: { enabled: canFetch }
  });

  const season = seasonQuery.data;
  const needsEndedSeason =
    canFetch &&
    season != null &&
    day != null &&
    !season.isInSeason &&
    season.seasonStart != null &&
    day < season.seasonStart &&
    Number(day.slice(5, 7)) <= 6;

  const endedQuery = noddiAsync.useGet({
    type: URLKeys.getStuddedTireSeason,
    input: {
      country: country ?? Country.NOR,
      zipCode: zipCode ?? undefined,
      asOf: day != null ? `${day.slice(0, 4)}-01-01` : undefined
    },
    queryConfig: { enabled: needsEndedSeason }
  });

  if (!canFetch || seasonQuery.isError || season == null || season.seasonStart == null || season.seasonEnd == null) {
    return null;
  }
  if (needsEndedSeason && endedQuery.isLoading) {
    return null;
  }

  const decision = spikedTireBookingSeason({
    bookingDate: day,
    asOf: season.asOf,
    seasonStart: season.seasonStart,
    seasonEnd: season.seasonEnd,
    isInSeason: season.isInSeason,
    endedSeasonEnd: endedQuery.data?.seasonEnd ?? null
  });
  const startLabel = dateLabel(season.seasonStart);
  const endedSeasonEnd = decision.outside === 'after' ? endedQuery.data?.seasonEnd : null;
  const endLabel = dateLabel(endedSeasonEnd ?? season.seasonEnd);
  const text = seasonText({ decision, bookingDate: day, startLabel, endLabel });

  return <NoddiInfoBanner variant='info' icon='MountainSnow' title={t`Studded tires`} texts={[{ value: text }]} />;
};

const seasonText = ({
  decision,
  bookingDate,
  startLabel,
  endLabel
}: {
  decision: SpikedTireBookingSeasonDecision;
  bookingDate: string | null;
  startLabel: string;
  endLabel: string;
}): string => {
  if (decision.early) {
    if (bookingDate != null) {
      return t`Studded tires are allowed on this date, ahead of the usual start on ${startLabel}.`;
    }
    return t`Studded tires are allowed now, ahead of the usual start on ${startLabel}.`;
  }
  if (decision.outside === 'before') {
    return t`Studded tires can be used from ${startLabel}, or when snow or road conditions require them.`;
  }
  if (decision.outside === 'after') {
    return t`Studded tires must be changed by ${endLabel}, unless snow or road conditions require them.`;
  }
  return t`Studded tires can be used from ${startLabel} until ${endLabel}, or when snow or road conditions require them.`;
};

export const MobileBookingSpikedTireSeasonBanner = () => {
  const cars = useBookingCars();
  const items = useBookingSelectedSalesItemsCars();
  const address = useBookingAddress();
  const timeWindow = useBookingTimeWindow();
  const active = cars.some((car) => car.hasStuddedWinterTires === true) && isWheelChangeOrTireHotel(items);

  return (
    <SpikedTireSeasonBanner
      active={active}
      countryCode={address?.countryCode}
      zipCode={address?.zipCode}
      bookingDate={apiDay(timeWindow?.startsAt)}
    />
  );
};

export const ProposalSpikedTireSeasonBanner = () => {
  const proposalType = useBookingProposalType();
  const items = useBookingProposalItems();
  const address = useBookingProposalAddress();
  const timeWindow = useBookingProposalTimeWindow();
  const wheelProposal =
    proposalType === BookingProposalType.RECALL_PROPOSAL_WHEEL_CHANGE ||
    proposalType === BookingProposalType.RECALL_PROPOSAL_TIRE_HOTEL ||
    items.some((item) =>
      item.salesItemsWithQuantity.some(
        (salesItem) => salesItem.salesItem.bookingCategoryType === SalesItemBookingCategoryType.WHEEL_SERVICES
      )
    );
  const spiked = items.some((item) => item.carWheelIdentifiers?.identifiers?.includes(CarWheelIdentifiers.IS_STUDDED));

  return (
    <SpikedTireSeasonBanner
      active={wheelProposal && spiked}
      countryCode={address?.countryCode}
      zipCode={address?.zipCode}
      bookingDate={apiDay(timeWindow?.startsAt)}
    />
  );
};

export const StationaryBookingSpikedTireSeasonBanner = () => {
  const car = useStationaryBookingCar();
  const primary = useStationaryPrimaryService();
  const addons = useStationaryAddonServices();
  const reservation = useStationaryReservation();
  const services = [primary, ...addons];
  const wheelService = services.some(
    (service) =>
      service?.bookingCategoryType === SalesItemBookingCategoryType.WHEEL_SERVICES ||
      service?.isWheelPickupRequired === true
  );
  // The service-center draft has no postal code, so this uses the country window.
  // A department-only early exception is not applied.

  return (
    <SpikedTireSeasonBanner
      active={car?.hasStuddedWinterTires === true && wheelService}
      countryCode={car?.licensePlate.countryCode}
      bookingDate={apiDay(reservation?.startsAt)}
    />
  );
};

type ExistingBookingProps = {
  booking: BookingRecordDetailsCustomer;
  bookingDate?: string | null;
};

export const ExistingBookingSpikedTireSeasonBanner = ({ booking, bookingDate }: ExistingBookingProps) => {
  const tireService = getIsTireServiceJob(booking);
  const spiked = useHasStuddedWinterTires(
    booking.bookingItemsCar.map((item) => item.licensePlate),
    tireService
  );

  return (
    <SpikedTireSeasonBanner
      active={tireService && spiked === true}
      countryCode={booking.address.countryCode}
      zipCode={booking.address.zipCode}
      bookingDate={bookingDate === undefined ? booking.date : bookingDate}
    />
  );
};
